import {
    ConditionalAccessApplications,
    ConditionalAccessClientApp,
    ConditionalAccessGrantControls,
    ConditionalAccessGuestOrExternalUserTypes,
    ConditionalAccessLocations,
    ConditionalAccessPlatforms,
    ConditionalAccessPolicy,
    ConditionalAccessSessionControls,
    NullableOption,
    RiskLevel,
} from '@microsoft/microsoft-graph-types-beta';
import { getCAPBaselineDisplayName } from './cap-schema';
import {
    createConditionalAccessPolicy,
    deleteConditionalAccessPolicy,
    updateConditionalAccessPolicy,
} from 'projects/angular-clarity/src/app/stores/client/graph/conditional-access/actions';

/**
 * Microsoft retired the "Require approved client app" grant (approvedApplication) on 2026-06-30: Graph refuses it on
 * create and edit, and a policy that has it can only be disabled or deleted (KRAK-479). "Require app protection
 * policy" (compliantApplication) replaces it. Same rule as the backend's `condiational-access-policy/utilities.ts`.
 */
export function replaceRetiredGrant<T extends NullableOption<ConditionalAccessGrantControls> | undefined>(grant: T): T {
    const controls = grant?.builtInControls as string[] | null | undefined;
    if (!grant || !controls?.includes('approvedApplication')) {
        return grant;
    }
    const replaced = [...new Set(controls.map((c) => (c === 'approvedApplication' ? 'compliantApplication' : c)))];
    return { ...grant, builtInControls: replaced } as T;
}

/** A policy with the retired grant is read-only in Microsoft: remediation deletes it and the next one recreates it. */
export function usesRetiredGrant(policy: ConditionalAccessPolicy | undefined): boolean {
    return !!(policy?.grantControls?.builtInControls as string[] | null | undefined)?.includes('approvedApplication');
}

const CAP_Policy = (items: {
    displayName;
    sessionControls: ConditionalAccessSessionControls;
    grantControls: NullableOption<ConditionalAccessGrantControls>;
    clientAppTypes: ConditionalAccessClientApp[];
    platforms: NullableOption<ConditionalAccessPlatforms>;
    applications: NullableOption<ConditionalAccessApplications>;
    locations: NullableOption<ConditionalAccessLocations>;
    signInRiskLevels: NullableOption<RiskLevel[]>;
    state: 'enabled' | 'disabled';
    includeRoles: string[];
    excludeRoles;
    includeUsers;
    excludeUsers;
    includeGroups;
    excludeGroups;
}): ConditionalAccessPolicy => {
    const {
        displayName,
        sessionControls,
        grantControls,
        clientAppTypes,
        signInRiskLevels,
        platforms,
        applications,
        locations,
        state,
        includeRoles,
        excludeRoles,
        includeUsers,
        excludeUsers,
        includeGroups,
        excludeGroups,
    } = items;

    return {
        displayName: displayName,
        state: state,
        conditions: {
            users: {
                includeRoles: includeRoles,
                excludeRoles: excludeRoles,
                includeUsers: includeUsers,
                excludeUsers: excludeUsers,
                includeGroups: includeGroups,
                excludeGroups: excludeGroups,
            },
            locations: locations,
            clientAppTypes: clientAppTypes,
            platforms: platforms,
            applications: applications,
            signInRiskLevels: signInRiskLevels,
        },

        sessionControls: sessionControls,
        grantControls: grantControls,
    };
};

export function RemediateCapItem(
    _tenant: string,
    schema: any,
    policy: ConditionalAccessPolicy,

    policy_change: {
        sessionControls?: NullableOption<ConditionalAccessSessionControls>;
        grantControls?: NullableOption<ConditionalAccessGrantControls>;
        clientAppTypes?: ConditionalAccessClientApp[];
        platforms?: NullableOption<ConditionalAccessPlatforms>;
        applications?: NullableOption<ConditionalAccessApplications>;
        locations?: NullableOption<ConditionalAccessLocations>;
        signInRiskLevels?: NullableOption<RiskLevel[]>;
    },
) {
    const displayName = getCAPBaselineDisplayName(schema);
    policy_change = { ...policy_change, grantControls: replaceRetiredGrant(policy_change.grantControls) };

    const isNoPolicySchema = !!schema?.not?.contains;
    const state = schema?.contains?.properties?.state?.const;

    const includeRoles =
        schema?.contains?.properties?.conditions?.properties?.users?.properties?.includeRoles?.items?.enum || [];

    const includeGuestsOrExternalUsersPattern: string =
        schema?.contains?.properties?.conditions?.properties?.users?.properties?.includeGuestsOrExternalUsers
            ?.properties?.guestOrExternalUserTypes?.pattern || null;
    const includeGuestsOrExternalUsers: string =
        includeGuestsOrExternalUsersPattern?.split(')')?.join('')?.replace('(?=.*', '').split('(?=.*')?.join(',') ||
        null;

    const excludeGuestsOrExternalUsersPattern: string =
        schema?.contains?.properties?.conditions?.properties?.users?.properties?.excludeGuestsOrExternalUsers
            ?.properties?.guestOrExternalUserTypes?.pattern || null;
    const excludeGuestsOrExternalUsers: string =
        excludeGuestsOrExternalUsersPattern?.split(')')?.join('')?.replace('(?=.*', '').split('(?=.*')?.join(',') ||
        null;

    const excludeRoles =
        schema?.contains?.properties?.conditions?.properties?.users?.properties?.excludeRoles?.items?.enum || [];
    const includeUsers =
        schema?.contains?.properties?.conditions?.properties?.users?.properties?.includeUsers?.items?.enum || [];
    const excludeUsers =
        schema?.contains?.properties?.conditions?.properties?.users?.properties?.excludeUsers?.items?.enum || [];
    const includeGroups =
        schema?.contains?.properties?.conditions?.properties?.users?.properties?.includeGroups?.items?.enum || [];
    const excludeGroups =
        schema?.contains?.properties?.conditions?.properties?.users?.properties?.excludeGroups?.items?.enum || [];

    if (!policy) {
        // create MFA Policy if it is not exists
        const new_policy: ConditionalAccessPolicy = JSON.parse(
            JSON.stringify(
                CAP_Policy({
                    displayName: displayName,
                    sessionControls: policy_change.sessionControls,
                    grantControls: policy_change.grantControls,
                    clientAppTypes: policy_change.clientAppTypes,
                    platforms: policy_change.platforms,
                    applications: policy_change.applications,
                    signInRiskLevels: policy_change.signInRiskLevels,
                    locations: policy_change.locations,
                    state,
                    includeRoles,
                    excludeRoles,
                    includeUsers,
                    excludeUsers,
                    includeGroups,
                    excludeGroups,
                }),
            ),
        );

        if (includeGuestsOrExternalUsers) {
            new_policy.conditions.users['includeGuestsOrExternalUsers'] = {};
            new_policy.conditions.users['includeGuestsOrExternalUsers'].guestOrExternalUserTypes =
                includeGuestsOrExternalUsers as ConditionalAccessGuestOrExternalUserTypes;
        } else {
            new_policy.conditions.users['includeGuestsOrExternalUsers'] = null;
        }

        if (excludeGuestsOrExternalUsers) {
            new_policy.conditions.users['excludeGuestsOrExternalUsers'] = {};
            new_policy.conditions.users['excludeGuestsOrExternalUsers'].guestOrExternalUserTypes =
                excludeGuestsOrExternalUsers as ConditionalAccessGuestOrExternalUserTypes;
        } else {
            new_policy.conditions.users['excludeGuestsOrExternalUsers'] = null;
        }

        return createConditionalAccessPolicy({ _tenant, policy: new_policy });
    } else if (policy) {
        if (isNoPolicySchema || usesRetiredGrant(policy)) {
            return deleteConditionalAccessPolicy({ _tenant, id: policy.id });
        } else {
            const updated_policy = JSON.parse(
                JSON.stringify(
                    CAP_Policy({
                        displayName: displayName,
                        sessionControls: policy_change.sessionControls,
                        grantControls: policy_change.grantControls,
                        clientAppTypes: policy_change.clientAppTypes,
                        platforms: policy_change.platforms,
                        applications: policy_change.applications,
                        signInRiskLevels: policy_change.signInRiskLevels,
                        locations: policy_change.locations,
                        state,
                        includeRoles,
                        excludeRoles,
                        includeUsers,
                        excludeUsers,
                        includeGroups,
                        excludeGroups,
                    }),
                ),
            );

            if (includeGuestsOrExternalUsers) {
                updated_policy.conditions.users['includeGuestsOrExternalUsers'] = {};
                updated_policy.conditions.users['includeGuestsOrExternalUsers'].guestOrExternalUserTypes =
                    includeGuestsOrExternalUsers;
            } else {
                updated_policy.conditions.users['includeGuestsOrExternalUsers'] = null;
            }

            if (excludeGuestsOrExternalUsers) {
                updated_policy.conditions.users['excludeGuestsOrExternalUsers'] = {};
                updated_policy.conditions.users['excludeGuestsOrExternalUsers'].guestOrExternalUserTypes =
                    excludeGuestsOrExternalUsers;
            } else {
                updated_policy.conditions.users['excludeGuestsOrExternalUsers'] = null;
            }

            return updateConditionalAccessPolicy({ _tenant, policy: { id: policy.id, ...updated_policy } });
        }
    }
}
